import { useRef } from "react";
import cx from "classnames";
import { faPrescription, faTag } from "@fortawesome/pro-regular-svg-icons";

import LinkButton from "common/components/LinkButton";
import CatalogImage from "common/components/CatalogImage";
import Icon from "common/components/Icon";

import TextChip from "client/common/components/content/TextChip";
import { urls } from "client/routes";
import { sendSelectItem, useSendPostHogProductClick } from "client/analytics/utils";
import { ProductListSource } from "client/analytics/types";

import { ProductSearchResult as ProductSearchResultType } from "../types";

import styles from "../styles/ProductSearchResult.module.less";

type ProductSearchResultV2Props = { result: ProductSearchResultType };

const ProductSearchResultV2 = ({ result }: ProductSearchResultV2Props) => {
  const resultRef = useRef<HTMLDivElement>(null);
  const sendPostHogProductClick = useSendPostHogProductClick();

  const hasPrescriptionLabel = Boolean(result.patient_prescription_label);
  const isFeatured =
    result.product_line.is_featured || result.product_line.is_loyalty_program_featured;
  const handleClick = () => {
    sendSelectItem(result, ProductListSource.SearchResults);
    sendPostHogProductClick({
      source: ProductListSource.SearchResults,
      id: result.id,
      product_line_id: result.product_line.id,
    });
  };

  return (
    <LinkButton
      dest={urls.products.detail({
        params: { id: result.product_line.id },
        search: { product: result.id },
      })}
      className={styles.container}
      onClick={handleClick}
    >
      <div className={styles.content} ref={resultRef}>
        {result.product_line.prescription_required && (
          <Icon fixedWidth={true} icon={faPrescription} className={styles.prescription} />
        )}
        <div
          className={cx(
            styles.background,
            hasPrescriptionLabel && styles.prescribed,
            !hasPrescriptionLabel && isFeatured && styles.featured,
          )}
        >
          {(hasPrescriptionLabel || isFeatured) &&
            (hasPrescriptionLabel ? (
              <TextChip variant="info" className={styles.chip}>
                {result.patient_prescription_label}
              </TextChip>
            ) : (
              <TextChip variant="highlight" className={styles.chip}>
                Your Vet&apos;s Choice
              </TextChip>
            ))}
          <CatalogImage
            image={result.main_image}
            size="medium"
            alt={result.name}
            className={styles.image}
          />
        </div>
        {result.promotion_label && (
          <div className={styles.promotion}>
            <Icon icon={faTag} /> {result.promotion_label}
          </div>
        )}
      </div>
      <div className={styles.name}>{result.full_name}</div>
    </LinkButton>
  );
};

export default ProductSearchResultV2;
